/** usePipelineSave — save/update pipeline flows and their dialog state. */

import React from 'react';

import { ToolRegistry } from '@/engine/ToolRegistry';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import { isVisualizer, resolveOutputType } from '@/interfaces/ITool';
import { useEditorStore } from '@/store/useEditorStore';
import { useExecutionStore } from '@/store/useExecutionStore';
import { usePipelineStore } from '@/store/usePipelineStore';
import { useToolBrowserStore } from '@/store/useToolBrowserStore';

export function usePipelineSave(
  setLoadedOutputSemanticType: React.Dispatch<React.SetStateAction<string | null>>,
  loadedPipelineOutputType: ISemanticType | null,
  resetStepSelection: () => void,
) {
  const savedPipelines = usePipelineStore((s) => s.savedPipelines);
  const savePipeline = usePipelineStore((s) => s.savePipeline);
  const updatePipeline = usePipelineStore((s) => s.updatePipeline);

  const activePipelineName = useEditorStore((s) => s.activePipelineName);
  const activePipelineDescription = useEditorStore((s) => s.activePipelineDescription);
  const activePipelineId = useEditorStore((s) => s.activePipelineId);
  const setActivePipelineName = useEditorStore((s) => s.setActivePipelineName);
  const setActivePipelineDescription = useEditorStore((s) => s.setActivePipelineDescription);
  const setActivePipelineId = useEditorStore((s) => s.setActivePipelineId);
  const setPipelineVariables = useEditorStore((s) => s.setPipelineVariables);
  const pipelineLength = useEditorStore((s) => s.pipeline.length);
  const inputData = useEditorStore((s) => s.input);

  const [saveOpen, setSaveOpen] = React.useState(false);
  const [saveChoiceOpen, setSaveChoiceOpen] = React.useState(false);
  const [saveMode, setSaveMode] = React.useState<'create' | 'update'>('create');
  const [saveName, setSaveName] = React.useState('');
  const [saveDescription, setSaveDescription] = React.useState('');
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saveIncludeInputData, setSaveIncludeInputData] = React.useState(false);

  const resetSaveForm = React.useCallback(() => {
    setSaveOpen(false);
    setSaveError(null);
    setSaveName('');
    setSaveDescription('');
    setSaveIncludeInputData(false);
  }, []);

  const buildPipelineSemanticTypes = React.useCallback(() => {
    const { inputType, pipeline } = useEditorStore.getState();
    const { stepResults } = useExecutionStore.getState();
    const inputSemanticType = inputType?.semanticType;

    const lastEnabledStep = [...pipeline].reverse().find((s) => {
      if (!s.isEnabled) return false;
      const tool = ToolRegistry.get(s.toolId);
      return tool ? !isVisualizer(tool) : true;
    });
    const lastTool = lastEnabledStep ? ToolRegistry.get(lastEnabledStep.toolId) : undefined;
    const inferredOutputSemanticType = lastTool
      ? resolveOutputType(lastTool, lastEnabledStep!.config)?.semanticType
      : undefined;

    const lastEnabledId = lastEnabledStep?.id;
    const lastResult = lastEnabledId
      ? [...stepResults].reverse().find((r) => r.stepId === lastEnabledId)
      : null;
    const pipelineDerivedType = lastResult?.outputType ?? null;

    const outputSemanticType =
      pipelineDerivedType?.semanticType ??
      inferredOutputSemanticType ??
      loadedPipelineOutputType?.semanticType;

    return { inputSemanticType, outputSemanticType };
  }, [loadedPipelineOutputType?.semanticType]);

  const buildPipelineStepsForSave = React.useCallback(() => {
    const { pipeline } = useEditorStore.getState();
    return pipeline.map(({ toolId, label, config, isEnabled }) => ({ toolId, label, config, isEnabled }));
  }, []);

  const openSavePipelineAsNew = React.useCallback(() => {
    setSaveError(null);
    setSaveChoiceOpen(false);
    resetStepSelection();
    setSaveName(activePipelineName ?? '');
    setSaveDescription(activePipelineDescription ?? '');
    setSaveOpen(true);
  }, [activePipelineDescription, activePipelineName, resetStepSelection]);

  const updateExistingPipeline = React.useCallback(() => {
    if (!activePipelineId) return;

    const existing = savedPipelines.find((p) => p.id === activePipelineId);
    const name = (activePipelineName ?? existing?.name ?? '').trim();
    if (!name) {
      openSavePipelineAsNew();
      return;
    }

    const description = (activePipelineDescription ?? existing?.description ?? '').trim() || undefined;
    const { inputSemanticType, outputSemanticType } = buildPipelineSemanticTypes();
    const steps = buildPipelineStepsForSave();
    const { pipelineVariables } = useEditorStore.getState();
    const variables = Object.keys(pipelineVariables).length > 0 ? pipelineVariables : undefined;

    updatePipeline(activePipelineId, { name, description, steps, inputSemanticType, outputSemanticType, variables });
    setActivePipelineName(name);
    setActivePipelineDescription(description ?? null);
    setActivePipelineId(activePipelineId);
    setLoadedOutputSemanticType(outputSemanticType ?? null);
    setSaveChoiceOpen(false);
    setSaveError(null);
  }, [
    activePipelineDescription, activePipelineId, activePipelineName,
    buildPipelineSemanticTypes, buildPipelineStepsForSave, openSavePipelineAsNew,
    savedPipelines, setActivePipelineDescription, setActivePipelineId,
    setActivePipelineName, setLoadedOutputSemanticType, updatePipeline,
  ]);

  const openSavePipeline = React.useCallback(() => {
    setSaveError(null);
    resetStepSelection();

    if (activePipelineId) {
      setSaveMode('update');
      setSaveName(activePipelineName ?? '');
      setSaveDescription(activePipelineDescription ?? '');
      setSaveOpen(true);
      return;
    }

    setSaveMode('create');
    setSaveName(activePipelineName ?? '');
    setSaveDescription(activePipelineDescription ?? '');
    setSaveOpen(true);
  }, [activePipelineDescription, activePipelineId, activePipelineName, resetStepSelection]);

  const handleSave = React.useCallback(() => {
    const trimmed = saveName.trim();
    if (!trimmed) {
      setSaveError('Name is required.');
      return;
    }

    const { inputSemanticType, outputSemanticType } = buildPipelineSemanticTypes();
    const { pipelineVariables, pipeline, input: currentInput, inputType } = useEditorStore.getState();
    const variables = Object.keys(pipelineVariables).length > 0 ? pipelineVariables : undefined;

    if (saveMode === 'update' && activePipelineId) {
      updatePipeline(activePipelineId, {
        name: trimmed,
        description: saveDescription.trim() || undefined,
        steps: buildPipelineStepsForSave(),
        inputSemanticType,
        outputSemanticType,
        variables,
        inputData: saveIncludeInputData && currentInput ? currentInput : undefined,
        inputType: saveIncludeInputData && inputType ? inputType.semanticType : undefined,
      });
      setActivePipelineName(trimmed);
      setActivePipelineDescription(saveDescription.trim() || null);
      setActivePipelineId(activePipelineId);
      setLoadedOutputSemanticType(outputSemanticType ?? null);
      resetSaveForm();
      return;
    }

    const id = savePipeline(trimmed, pipeline, saveDescription.trim() || undefined, undefined, {
      inputSemanticType,
      outputSemanticType,
      variables,
      inputData: saveIncludeInputData && currentInput ? currentInput : undefined,
      inputType: saveIncludeInputData && inputType ? inputType.semanticType : undefined,
    });
    setActivePipelineName(trimmed);
    setActivePipelineDescription(saveDescription.trim() || null);
    setActivePipelineId(id);
    setPipelineVariables(pipelineVariables);
    resetSaveForm();
    useToolBrowserStore.getState().setMode('pipelines');
  }, [
    activePipelineId, buildPipelineSemanticTypes, buildPipelineStepsForSave,
    resetSaveForm, saveDescription, saveIncludeInputData, saveMode, saveName,
    savePipeline, setActivePipelineDescription, setActivePipelineId,
    setActivePipelineName, setLoadedOutputSemanticType, setPipelineVariables,
    updatePipeline,
  ]);

  const saveDialogProps = React.useMemo(
    () => ({
      open: saveOpen,
      name: saveName,
      description: saveDescription,
      error: saveError,
      canSave: pipelineLength > 0,
      includeInputData: saveIncludeInputData,
      inputDataSize: new TextEncoder().encode(inputData).length,
      mode: saveMode,
      onChangeName: setSaveName,
      onChangeDescription: setSaveDescription,
      onChangeIncludeInputData: setSaveIncludeInputData,
      onClose: () => {
        setSaveOpen(false);
        setSaveError(null);
        setSaveIncludeInputData(false);
      },
      onSave: handleSave,
      onSaveAsNew: () => { setSaveMode('create'); },
    }),
    [saveOpen, saveName, saveDescription, saveError, pipelineLength, saveIncludeInputData, inputData, saveMode, handleSave]
  );

  const saveChoiceDialogProps = React.useMemo(
    () => ({
      open: saveChoiceOpen,
      pipelineName: activePipelineName,
      onCancel: () => setSaveChoiceOpen(false),
      onUpdateExisting: updateExistingPipeline,
      onSaveAsNew: openSavePipelineAsNew,
    }),
    [saveChoiceOpen, activePipelineName, updateExistingPipeline, openSavePipelineAsNew]
  );

  return { openSavePipeline, saveDialogProps, saveChoiceDialogProps };
}
